import { CheckCircle2, X, FileText, Phone, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader } from '../ui/card';
import { useLanguage } from '../../contexts/LanguageContext';
import { CONTACT_INFO } from '../../constants/navigation';
import { copyTextToClipboard } from '../../utils/copyToClipboard';
import type { Car } from '../Listings';

interface BuyingFormSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  car?: Car;
  onBrowseMore?: () => void;
}

export function BuyingFormSuccessModal({ 
  isOpen, 
  onClose, 
  car,
  onBrowseMore 
}: BuyingFormSuccessModalProps) {
  const { t } = useLanguage();

  if (!isOpen) return null;

  // Generate a mock reference number based on car and timestamp
  const referenceNumber = `SAY-${car?.id || '000'}-${Date.now().toString().slice(-6)}`;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      onClick={handleOverlayClick}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300" />
      
      <Card className="relative z-10 w-full max-w-2xl mx-4 border-[#f91bdc]/20 shadow-xl animate-in zoom-in-95 duration-300">
        <CardHeader className="relative pb-6">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onClose}
            className="absolute right-4 top-4 h-8 w-8 p-0 hover:bg-gray-100"
          >
            <X size={18} />
          </Button>
        </CardHeader>
        
        <CardContent className="space-y-6 pb-8">
          {/* Success Icon and Title */}
          <div className="text-center space-y-4">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle2 size={40} className="text-green-600" />
            </div>
            
            <div className="space-y-2">
              <h2 className="text-2xl font-semibold text-gray-900">
                {t('buyingForm.success.title')}
              </h2>
              <p className="text-gray-600 leading-relaxed max-w-lg mx-auto">
                {t('buyingForm.success.message')}
              </p>
            </div>
          </div>

          {/* Reference Number */}
          <div className="bg-[#f91bdc]/5 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <FileText size={20} className="text-[#f91bdc]" />
              <span className="font-medium text-gray-900">
                {t('buyingForm.success.referenceNumber')}
              </span>
            </div>
            <span className="text-lg font-mono font-semibold text-[#f91bdc]">
              {referenceNumber}
            </span>
          </div>

          {/* Car Information */}
          {car && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2">
                {t('buyingForm.success.vehicleDetails')}
              </h3>
              <p className="text-gray-700">
                {car.year} {car.make} {car.model} - ${car.totalPrice?.toLocaleString()}
              </p>
            </div>
          )}

          {/* Next Steps */}
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900">
              {t('buyingForm.success.nextSteps')}
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-[#f91bdc] text-white rounded-full flex items-center justify-center text-xs font-semibold mt-0.5">
                  1
                </div>
                <p className="text-gray-700">
                  {t('buyingForm.success.step1')}
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-[#f91bdc] text-white rounded-full flex items-center justify-center text-xs font-semibold mt-0.5">
                  2
                </div>
                <p className="text-gray-700">
                  {t('buyingForm.success.step2')}
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-[#f91bdc] text-white rounded-full flex items-center justify-center text-xs font-semibold mt-0.5">
                  3
                </div>
                <p className="text-gray-700">
                  {t('buyingForm.success.step3')}
                </p>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="border-t pt-4">
            <p className="text-center text-sm text-gray-600 mb-3">
              {t('buyingForm.success.contactInfo')}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-2 sm:gap-4">
              <button
                type="button"
                className="flex items-center justify-center space-x-2 text-[#f91bdc] hover:text-[#e015c9] transition-colors duration-200 text-sm"
                onClick={async () => {
                  const ok = await copyTextToClipboard(CONTACT_INFO.phone);
                  toast[ok ? 'success' : 'error'](ok ? t('common.copiedToClipboard') : t('common.copyFailed'));
                }}
              >
                <Phone size={16} />
                <span>{CONTACT_INFO.phone}</span>
              </button>
              <button
                type="button"
                className="flex items-center justify-center space-x-2 text-[#f91bdc] hover:text-[#e015c9] transition-colors duration-200 text-sm"
                onClick={async () => {
                  const ok = await copyTextToClipboard(CONTACT_INFO.email);
                  toast[ok ? 'success' : 'error'](ok ? t('common.copiedToClipboard') : t('common.copyFailed'));
                }}
              >
                <Mail size={16} />
                <span>{CONTACT_INFO.email}</span>
              </button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            {onBrowseMore && (
              <Button
                onClick={onBrowseMore}
                variant="outline"
                className="flex-1 border-[#f91bdc] text-[#f91bdc] hover:bg-[#f91bdc]/10"
              >
                {t('buyingForm.success.backToListings')}
              </Button>
            )}
            <Button 
              onClick={onClose}
              className="flex-1 bg-[#f91bdc] hover:bg-[#e015c9] text-white"
            >
              {t('buyingForm.success.close')}
            </Button>
          </div>

          {/* Security Notice */}
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <p className="text-sm text-blue-800">
              🔒 {t('buyingForm.success.securityNotice')}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}